import React from 'react';
import { CHART_COLORS } from './chartTokens';

// A small line with no axis: the shape of a value over time, beside its number.
// 'trend' (the KpiTile trend): a gray line, and the last step in blue, so the eye goes to the change of now.
// 'direction' (a table cell, many days): one line in gray-500, red-600 when the last value is below the first one
// (the value fell over the window), and a dot on the last value. Direction only: red is "lower", not "bad".
// A null value is a gap. With fewer than 2 values, nothing shows. The line is decoration (aria-hidden): the caller
// gives the values as text too.

export type SparklineVariant = 'trend' | 'direction';

type Props = {
	// Oldest first. The last value is the current one.
	values: (number | null)[],
	// Default 'trend'.
	variant?: SparklineVariant,
	// The size and the place of the svg, for example "mt-2 block h-7 w-full".
	className?: string,
};

const W = 100;
const H = 28;

// The first and the last known value: the change over the window, or null with fewer than 2 values.
export const sparklineChange = (values: (number | null)[]): number | null => {
	const known = values.filter((v): v is number => v !== null && !Number.isNaN(v));
	return known.length < 2 ? null : known[known.length - 1] - known[0];
};

export const Sparkline: React.FunctionComponent<Props> = ({ values, variant = 'trend', className }) => {
	const known = values.filter((v): v is number => v !== null);
	if (known.length < 2) return null;
	const lo = Math.min(...known);
	const hi = Math.max(...known);
	const x = (i: number) => values.length === 1 ? W / 2 : i / (values.length - 1) * W;
	const y = (v: number) => hi === lo ? H / 2 : 2 + (1 - (v - lo) / (hi - lo)) * (H - 4);
	const points = values.map((v, i) => v === null ? null : `${x(i)},${y(v)}`);
	// The line, cut at each null value. 'trend' leaves out the last step (it is blue).
	const runsOf = (list: (string | null)[]): string[] => {
		const runs: string[] = [];
		let run: string[] = [];
		list.forEach(p => {
			if (p === null) { if (run.length > 1) runs.push(run.join(' ')); run = []; }
			else run.push(p);
		});
		if (run.length > 1) runs.push(run.join(' '));
		return runs;
	};
	if (variant === 'direction') {
		const color = (sparklineChange(values) ?? 0) < 0 ? CHART_COLORS.down : CHART_COLORS.compset;
		const lastIndex = values.map(v => v !== null).lastIndexOf(true);
		const end = { x: x(lastIndex), y: y(values[lastIndex] as number) };
		return <svg aria-hidden="true" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" overflow="visible" className={className}>
			{runsOf(points).map(r => <polyline key={r} points={r} fill="none" stroke={color} strokeWidth={1.5}
				strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />)}
			{/* A round cap on a very short line: a dot that the stretch of the svg does not change into an oval. */}
			<path d={`M${end.x - 0.01} ${end.y}L${end.x} ${end.y}`} stroke={color} strokeWidth={4} strokeLinecap="round"
				vectorEffect="non-scaling-stroke" />
		</svg>;
	}
	const last = points[points.length - 1];
	const beforeLast = points[points.length - 2];
	return <svg aria-hidden="true" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className={className}>
		{runsOf(points.slice(0, -1)).map(r => <polyline key={r} points={r} fill="none" stroke={CHART_COLORS.other} strokeWidth={1.5}
			strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />)}
		{last !== null && beforeLast !== null && <polyline points={`${beforeLast} ${last}`} fill="none" stroke={CHART_COLORS.own}
			strokeWidth={2} strokeLinecap="round" vectorEffect="non-scaling-stroke" />}
	</svg>;
};

export default Sparkline;
